import {
  BcfServicesIntegrationType,
  GetProjectExtensionsResponse,
} from "@faro-lotv/service-wires";
import { useLayoutEffect, useState } from "react";

/**
 * A record mapping annotation types to the status ID that should be hardcoded.
 * The status ID is matched case-insensitively against the project extensions.
 */
export type HardcodedStatusIds = Partial<
  Record<BcfServicesIntegrationType, string>
>;

/**
 * For certain annotation types, we only support creating annotations with a specific status.
 * This hook initializes the status field to the hardcoded status from the project extensions
 * and returns whether the status is hardcoded.
 *
 * @param externalAnnotationType The current annotation type.
 * @param hardcodedStatusIds A record mapping annotation types to their hardcoded status ID.
 * @param projectExtensions The project extensions containing available statuses.
 * @param onStatusChange Callback to set the status value.
 * @returns Whether the status is hardcoded and the dropdown should be disabled.
 */
export function useHardcodedStatus(
  externalAnnotationType: BcfServicesIntegrationType,
  hardcodedStatusIds: HardcodedStatusIds,
  projectExtensions: GetProjectExtensionsResponse,
  onStatusChange?: (status: string) => void,
): boolean {
  const [isStatusHardcoded, setIsStatusHardcoded] = useState(false);

  const targetStatusId = hardcodedStatusIds[externalAnnotationType];

  // Uses a layout effect to avoid flickering of the status field.
  useLayoutEffect(() => {
    if (!targetStatusId) {
      setIsStatusHardcoded(false);
      return;
    }

    const defaultStatus = projectExtensions.topic_status.find(
      (s) => s.id.toLowerCase() === targetStatusId.toLowerCase(),
    );

    // Fall back to allowing the user to choose the status, if the expected status is not available.
    if (!defaultStatus) {
      setIsStatusHardcoded(false);
      return;
    }

    onStatusChange?.(defaultStatus.id);
    setIsStatusHardcoded(true);
  }, [targetStatusId, onStatusChange, projectExtensions]);

  return isStatusHardcoded;
}
